import * as React from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  BarChart3,
  BookCopy,
  Brain,
  ClipboardList,
  History,
  Home,
  Library,
  LogOut,
  PenLine,
  Settings,
  Shield,
  Users,
  Timer,
  MoreHorizontal,
} from 'lucide-react';
import { useT } from '@/i18n';
import { cn } from '@/lib/utils';
import { post } from '@/lib/api';
import { forgetLastMe, useMe } from '@/lib/auth';
import { drafts } from '@/lib/drafts';
import { useOnline } from '@/hooks/useSync';
import { Menu, MenuContent, MenuItem, MenuSeparator, MenuTrigger } from '@/components/ui/misc';
import { ConfirmDialog } from '@/components/ui/dialog';
import { BuildLine } from '@/components/BuildInfo';
import { CloudOff } from 'lucide-react';

interface NavItem {
  to: string;
  key: string;
  icon: React.ReactNode;
  end?: boolean;
}

export function useNavItems(): { main: NavItem[]; practice: NavItem[]; extra: NavItem[] } {
  const me = useMe();
  const roles = me.data?.user.roles ?? [];
  const extra: NavItem[] = [];
  if (roles.includes('teacher')) extra.push({ to: '/shared', key: 'nav.shared', icon: <Users /> });
  if (roles.includes('admin')) extra.push({ to: '/admin', key: 'nav.admin', icon: <Shield /> });
  return {
    main: [
      { to: '/', key: 'nav.home', icon: <Home />, end: true },
      { to: '/library', key: 'nav.library', icon: <Library /> },
      { to: '/history', key: 'nav.history', icon: <History /> },
      { to: '/progress', key: 'nav.progress', icon: <BarChart3 /> },
    ],
    practice: [
      { to: '/practice/copy', key: 'nav.copy', icon: <BookCopy /> },
      { to: '/practice/recall', key: 'nav.recall', icon: <Brain /> },
      { to: '/practice/free', key: 'nav.free', icon: <PenLine /> },
      { to: '/exam', key: 'nav.exam', icon: <Timer /> },
    ],
    extra,
  };
}

function SideLink({ item }: { item: NavItem }) {
  const t = useT();
  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={({ isActive }) =>
        cn(
          'flex h-10 items-center gap-3 rounded-md px-3 text-[14px] font-medium transition [&_svg]:size-[18px]',
          isActive ? 'bg-primary-soft text-primary' : 'text-ink-2 hover:bg-paper-2',
        )
      }
    >
      {item.icon}
      {t(item.key)}
    </NavLink>
  );
}

export function useSignOut() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [ask, setAsk] = React.useState<number | null>(null);
  const run = async () => {
    try {
      await post('/auth/logout');
    } catch {
      /* already signed out */
    }
    await drafts.clearAll();
    forgetLastMe();
    qc.clear();
    navigate('/login', { replace: true });
  };
  const start = async () => {
    const n = await drafts.unsyncedCount();
    if (n > 0) setAsk(n);
    else await run();
  };
  const dialog = <SignOutDialog count={ask} onCancel={() => setAsk(null)} onConfirm={() => void run()} />;
  return { start, dialog };
}

function SignOutDialog({ count, onCancel, onConfirm }: { count: number | null; onCancel: () => void; onConfirm: () => void }) {
  const t = useT();
  return (
    <ConfirmDialog
      open={count !== null}
      onOpenChange={(o) => !o && onCancel()}
      title={t('auth.signOutUnsyncedTitle')}
      description={t('auth.signOutUnsyncedText', { n: count ?? 0 })}
      confirmLabel={t('common.signOut')}
      cancelLabel={t('common.cancel')}
      danger
      onConfirm={onConfirm}
    />
  );
}

/**
 * App chrome: a calm sidebar on iPad landscape/desktop, a bottom tab bar on portrait/phone.
 * The writing workspace renders outside this shell for a distraction-free canvas.
 */
export function AppShell() {
  const t = useT();
  const me = useMe();
  const online = useOnline();
  const nav = useNavItems();
  const signOut = useSignOut();
  const user = me.data?.user;

  return (
    <div className="flex h-dvh bg-paper pt-safe">
      <aside className="hidden w-64 shrink-0 flex-col border-r border-border bg-surface/70 lg:flex">
        <div className="flex h-16 items-center gap-2.5 px-5">
          <div className="grid size-9 place-items-center rounded-[10px] bg-primary text-[17px] font-semibold text-primary-ink greek">α</div>
          <div>
            <div className="text-[15px] font-semibold leading-tight">{t('app.name')}</div>
            <div className="text-[11.5px] text-muted">{t('app.tagline')}</div>
          </div>
        </div>
        <nav className="flex-1 space-y-6 overflow-y-auto px-3 py-2" aria-label={t('nav.menu')}>
          <div className="space-y-0.5">{nav.main.map((i) => <SideLink key={i.to} item={i} />)}</div>
          <div>
            <div className="px-3 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-2">{t('nav.practice')}</div>
            <div className="space-y-0.5">{nav.practice.map((i) => <SideLink key={i.to} item={i} />)}</div>
          </div>
          {nav.extra.length > 0 && <div className="space-y-0.5">{nav.extra.map((i) => <SideLink key={i.to} item={i} />)}</div>}
        </nav>
        <div className="border-t border-border p-3">
          {!online && (
            <div className="mb-2 flex items-center gap-2 rounded-md bg-warn-soft px-3 py-2 text-[12.5px] text-warn">
              <CloudOff className="size-4" /> {t('common.offline')}
            </div>
          )}
          <Menu>
            <MenuTrigger asChild>
              <button className="flex w-full items-center gap-3 rounded-md p-2 text-left hover:bg-paper-2" data-testid="user-menu">
                <span className="grid size-9 place-items-center rounded-full bg-accent-soft text-[14px] font-semibold text-accent">
                  {(user?.displayName ?? user?.email ?? '?').slice(0, 1).toUpperCase()}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13.5px] font-medium">{user?.displayName ?? user?.email ?? '—'}</span>
                  <span className="block truncate text-[11.5px] text-muted">{user?.roles.join(' · ')}</span>
                </span>
              </button>
            </MenuTrigger>
            <MenuContent side="top" align="start" className="w-56">
              <MenuItem asChild>
                <NavLink to="/settings">
                  <Settings /> {t('nav.settings')}
                </NavLink>
              </MenuItem>
              <MenuSeparator />
              <MenuItem onSelect={() => void signOut.start()} data-testid="sign-out">
                <LogOut /> {t('common.signOut')}
              </MenuItem>
            </MenuContent>
          </Menu>
          <BuildLine className="mt-2 truncate px-2 text-[10.5px] text-muted-2" />
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* compact top bar for portrait / phone */}
        <header className="flex h-14 shrink-0 items-center gap-2 border-b border-border bg-surface/80 px-4 backdrop-blur lg:hidden">
          <div className="grid size-8 place-items-center rounded-[9px] bg-primary text-[15px] font-semibold text-primary-ink greek">α</div>
          <div className="mr-auto text-[15px] font-semibold">{t('app.name')}</div>
          {!online && (
            <span className="inline-flex items-center gap-1 rounded-full bg-warn-soft px-2.5 py-1 text-[12px] text-warn">
              <CloudOff className="size-3.5" /> {t('common.offline')}
            </span>
          )}
          <Menu>
            <MenuTrigger asChild>
              <button className="grid size-10 place-items-center rounded-full bg-accent-soft text-[14px] font-semibold text-accent" aria-label={t('nav.menu')}>
                {(user?.displayName ?? user?.email ?? '?').slice(0, 1).toUpperCase()}
              </button>
            </MenuTrigger>
            <MenuContent align="end" className="w-60">
              {[...nav.practice.slice(1), ...nav.extra].map((i) => (
                <MenuItem key={i.to} asChild>
                  <NavLink to={i.to}>
                    {i.icon} {t(i.key)}
                  </NavLink>
                </MenuItem>
              ))}
              <MenuItem asChild>
                <NavLink to="/settings">
                  <Settings /> {t('nav.settings')}
                </NavLink>
              </MenuItem>
              <MenuSeparator />
              <MenuItem onSelect={() => void signOut.start()}>
                <LogOut /> {t('common.signOut')}
              </MenuItem>
            </MenuContent>
          </Menu>
        </header>
        <main className="min-h-0 flex-1 overflow-y-auto pb-[calc(72px+var(--safe-bottom))] lg:pb-0" id="main">
          <Outlet />
        </main>
        {/* bottom tab bar */}
        <nav className="fixed inset-x-0 bottom-0 z-30 flex border-t border-border bg-surface/95 pb-safe backdrop-blur lg:hidden" aria-label={t('nav.menu')}>
          {([nav.main[0]!, { to: '/practice/copy', key: 'nav.practice', icon: <ClipboardList /> }, nav.main[2]!, nav.main[3]!, { to: '/library', key: 'nav.library', icon: <MoreHorizontal /> }] as NavItem[]).map((i) => (
            <NavLink
              key={i.key}
              to={i.to}
              end={i.end}
              className={({ isActive }) =>
                cn('flex h-16 flex-1 flex-col items-center justify-center gap-1 text-[11px] font-medium [&_svg]:size-[22px]', isActive ? 'text-primary' : 'text-muted')
              }
            >
              {i.icon}
              {t(i.key)}
            </NavLink>
          ))}
        </nav>
      </div>
      {signOut.dialog}
    </div>
  );
}
